"use client";

import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell, Legend } from "recharts";
import { ChartCard } from "./chart-card";
import type { BreakdownEntry } from "@/types/analytics";

interface StatusCodeChartProps {
  entries: BreakdownEntry[];
  activeStatusCodes: number[] | undefined;
  isLoading: boolean;
  onSelectStatusCode: (statusCode: number) => void;
}

export function StatusCodeChart({ entries, activeStatusCodes, isLoading, onSelectStatusCode }: StatusCodeChartProps) {
  const activeSet = new Set(activeStatusCodes ?? []);

  return (
    <ChartCard
      title="Errors by status code"
      description="Click a bar to filter the dashboard to that status code."
      isLoading={isLoading}
      isEmpty={!isLoading && entries.length === 0}
      emptyMessage="No status code errors in the current results."
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={entries} margin={{ bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis dataKey="key" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={{ stroke: "var(--border)" }} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
          <Tooltip
            cursor={{ fill: "var(--accent)" }}
            contentStyle={{
              background: "var(--popover)",
              color: "var(--popover-foreground)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} formatter={() => "Errors"} />
          <Bar
            dataKey="totalErrors"
            name="Errors"
            radius={[4, 4, 0, 0]}
            cursor="pointer"
            onClick={(entry) => {
              const key = (entry as unknown as BreakdownEntry).key;
              const statusCode = Number.parseInt(key, 10);
              if (Number.isFinite(statusCode)) onSelectStatusCode(statusCode);
            }}
          >
            {entries.map((entry) => (
              <Cell
                key={entry.key}
                fill="var(--destructive)"
                fillOpacity={activeSet.size === 0 || activeSet.has(Number.parseInt(entry.key, 10)) ? 1 : 0.35}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
